import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { listCouponsByAffiliate } from '@/modules/discount-coupons/services/couponsService'
import { Table, Tr, Th, Td } from '@/shared/ui/Table'
import { Badge, Tooltip, Button } from '@/shared/ui'
import { useToast } from '@/shared/ui/Toast'
import { Copy } from 'lucide-react'

function formatMoney(cents?: number | null, currency?: string | null) {
  const value = Math.max(0, Number(cents || 0)) / 100
  const curr = (currency || 'BRL').toUpperCase()
  try { return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: curr as any }).format(value) } catch { return `R$ ${value.toFixed(2)}` }
}

function StatusBadge({ row }: { row: any }) {
  const now = Date.now()
  const startMs = row.start_at ? new Date(row.start_at).getTime() : null
  const endMs = row.end_at ? new Date(row.end_at).getTime() : null
  const scheduled = (row.is_active !== false) && !!startMs && startMs > now
  const enabled = (typeof row.is_live === 'boolean')
    ? !!row.is_live
    : ((row.is_active !== false) && (!startMs || startMs <= now) && (!endMs || endMs >= now))
  const color: any = scheduled ? 'warning' : (enabled ? 'success' : 'error')
  const label = scheduled ? 'agendado' : (enabled ? 'ativo' : 'inativo')
  const tip = scheduled
    ? `Cupom agendado. Válido a partir de ${startMs ? new Date(startMs).toLocaleString('pt-BR') : '-'}.`
    : (enabled
        ? 'Cupom ativo. Aplicável agora e dentro da validade.'
        : `Cupom inativo (desligado ou fora da validade${endMs && endMs < now ? ' — expirado' : ''}).`)
  return (
    <Tooltip content={tip}>
      <Badge variant="soft" color={color}>{label}</Badge>
    </Tooltip>
  )
}

export default function MyCouponsPage() {
  const { profile } = useAuth()
  const uid = profile?.id || ''
  const [rows, setRows] = React.useState<any[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const { add: toast } = useToast()

  React.useEffect(() => { (async () => {
    if (!uid) return
    setLoading(true); setError(null)
    try { const list = await listCouponsByAffiliate(uid); setRows(list) } catch (e:any) { setError(e?.message || 'Falha ao carregar cupons') } finally { setLoading(false) }
  })() }, [uid])

  return (
    <div className="w-full">
      <h1 className="mb-4 text-2xl font-semibold">Meus cupons</h1>
      {error && <div className="mb-3 rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">{error}</div>}
      {loading ? (
        <div className="text-sm text-foreground/70">Carregando...</div>
      ) : (
        <Table>
          <thead>
            <Tr>
              <Th>Nome</Th>
              <Th>Código</Th>
              <Th>Desconto</Th>
              <Th>Início</Th>
              <Th>Vencimento</Th>
              <Th>Status</Th>
            </Tr>
          </thead>
          <tbody>
            {rows.map((c) => (
              <Tr key={c.id}>
                <Td className="font-medium">{c.name}</Td>
                <Td>
                  <div className="flex items-center gap-2">
                    <span className="font-mono">{c.code}</span>
                    <Tooltip content="Copiar código">
                      <Button
                        aria-label="Copiar código"
                        variant="ghost"
                        className="h-8 w-8 p-0"
                        onClick={async()=>{ try { await navigator.clipboard.writeText(c.code||''); toast({ title: 'Copiado', description: 'Código copiado para a área de transferência', variant: 'success', soft: true }) } catch { toast({ title: 'Falha ao copiar', variant: 'error', soft: true }) } }}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </Tooltip>
                  </div>
                </Td>
                <Td>{c.discount_type==='percent' ? `${c.percent_off||0}%` : formatMoney(c.amount_off_cents, c.currency)}</Td>
                <Td>{c.start_at ? new Date(c.start_at).toLocaleString('pt-BR') : '-'}</Td>
                <Td>{c.end_at ? new Date(c.end_at).toLocaleDateString('pt-BR') : '—'}</Td>
                <Td><StatusBadge row={c} /></Td>
              </Tr>
            ))}
            {rows.length===0 && (
              <Tr><Td colSpan={6} className="py-6 text-center text-sm text-foreground/70">Nenhum cupom vinculado.</Td></Tr>
            )}
          </tbody>
        </Table>
      )}
    </div>
  )
}
